import {
  fetchPushNotificationConfig,
  registerPushSubscription,
  removePushSubscription,
  type PushSubscriptionPayload,
} from "@/api/autoparts";
import {
  getAppServiceWorkerRegistration,
  recoverAppServiceWorkerRegistration,
} from "@/lib/app-update";

export type ReportPushNotificationState =
  | "unsupported"
  | "unconfigured"
  | "unavailable"
  | "denied"
  | "disabled"
  | "enabled";

type PushRegistrationFailureKind =
  | "permission-denied"
  | "service-worker-not-ready"
  | "push-service-unavailable"
  | "offline"
  | "invalid-vapid"
  | "unsupported"
  | "unknown";

class PushRegistrationError extends Error {
  readonly kind: PushRegistrationFailureKind;

  constructor(kind: PushRegistrationFailureKind, message: string) {
    super(message);
    this.name = "PushRegistrationError";
    this.kind = kind;
  }
}

type PushDiagnosticDetails = {
  registration?: ServiceWorkerRegistration | null;
  getSubscriptionSucceeded?: boolean;
  existingSubscription?: boolean;
  vapidKeyMatches?: boolean;
};

function hasReportPushNotificationApis(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

function isSecurePushContext(): boolean {
  return typeof window !== "undefined" && window.isSecureContext;
}

function isBraveBrowser(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "brave" in (navigator as Navigator & { brave?: unknown })
  );
}

function errorDetails(error: unknown): { name: string; message: string } {
  return error instanceof Error
    ? { name: error.name, message: error.message }
    : { name: "UnknownError", message: String(error) };
}

function logPushDiagnostic(
  context: string,
  error: unknown,
  details: PushDiagnosticDetails = {},
): void {
  const registration = details.registration ?? null;
  const diagnostic = {
    ...errorDetails(error),
    permission:
      typeof Notification !== "undefined" ? Notification.permission : "unavailable",
    secureContext: typeof window !== "undefined" ? window.isSecureContext : false,
    online: typeof navigator !== "undefined" ? navigator.onLine : false,
    registrationScope: registration?.scope ?? null,
    activeState: registration?.active?.state ?? null,
    activeScriptURL: registration?.active?.scriptURL ?? null,
    controllerScriptURL:
      typeof navigator !== "undefined"
        ? navigator.serviceWorker?.controller?.scriptURL ?? null
        : null,
    getSubscriptionSucceeded: details.getSubscriptionSucceeded,
    existingSubscription: details.existingSubscription,
    vapidKeyMatches: details.vapidKeyMatches,
  };

  console.error(`[sparelynk-push] ${context}`, diagnostic);
  if (import.meta.env.DEV && error instanceof Error) {
    console.error(`[sparelynk-push] ${context} original error`, error);
  }
}

async function logPushRegistrationFailure(
  context: string,
  error: unknown,
  registration: ServiceWorkerRegistration,
  expectedKey: Uint8Array,
): Promise<void> {
  let getSubscriptionSucceeded = false;
  let existingSubscription = false;
  let vapidKeyMatches: boolean | undefined;

  try {
    const subscription = await registration.pushManager.getSubscription();
    getSubscriptionSucceeded = true;
    existingSubscription = Boolean(subscription);
    if (subscription) {
      vapidKeyMatches = subscriptionUsesVapidKey(subscription, expectedKey);
    }
  } catch {
    // The original failure remains the primary diagnostic. This only records whether
    // the browser can still inspect its subscription state after that failure.
  }

  logPushDiagnostic(context, error, {
    registration,
    getSubscriptionSucceeded,
    existingSubscription,
    vapidKeyMatches,
  });
}

function decodeUrlSafeBase64(value: string): Uint8Array {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error("Push notification server key is empty");
  }

  const normalized = trimmed.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "=",
  );
  const binary = window.atob(padded);
  const output = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    output[index] = binary.charCodeAt(index);
  }
  return output;
}

function decodeAndValidateVapidPublicKey(value: string): Uint8Array {
  let key: Uint8Array;
  try {
    key = decodeUrlSafeBase64(value);
  } catch (error) {
    logPushDiagnostic("invalid VAPID public key encoding", error);
    throw new Error("SpareLynk notifications are not configured correctly on this server");
  }

  if (key.length !== 65 || key[0] !== 0x04) {
    logPushDiagnostic(
      "invalid VAPID public key shape",
      new Error(`expected 65-byte uncompressed P-256 key, received ${key.length} bytes`),
    );
    throw new Error("SpareLynk notifications are not configured correctly on this server");
  }

  return key;
}

function encodeUrlSafeBase64(value: ArrayBuffer | null): string {
  if (!value) return "";
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function serializeSubscription(
  subscription: PushSubscription,
): PushSubscriptionPayload {
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: encodeUrlSafeBase64(subscription.getKey("p256dh")),
      auth: encodeUrlSafeBase64(subscription.getKey("auth")),
    },
  };
}

function byteArraysEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

function subscriptionUsesVapidKey(
  subscription: PushSubscription,
  expectedKey: Uint8Array,
): boolean {
  const applicationServerKey = subscription.options.applicationServerKey;
  if (!applicationServerKey) {
    return false;
  }
  return byteArraysEqual(new Uint8Array(applicationServerKey), expectedKey);
}

async function removeStaleSubscription(
  subscription: PushSubscription,
): Promise<void> {
  const endpoint = subscription.endpoint;
  try {
    const unsubscribed = await subscription.unsubscribe();
    if (!unsubscribed) {
      throw new Error("Browser did not remove the stale push subscription");
    }
  } catch (error) {
    logPushDiagnostic("unable to remove stale browser subscription", error);
    throw new Error(
      "Unable to replace an outdated browser notification subscription. Reload the page and try again.",
    );
  }

  try {
    await removePushSubscription(endpoint);
  } catch (error) {
    // The browser subscription is already gone. The backend also removes stale endpoints after 404/410 delivery responses.
    logPushDiagnostic("unable to remove stale backend subscription", error);
  }
}

function isPushServiceRegistrationError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /registration failed\s*-\s*push service error/i.test(error.message);
}

function isControlledRetryCandidate(error: unknown): boolean {
  return (
    isPushServiceRegistrationError(error) ||
    (error instanceof DOMException &&
      (error.name === "AbortError" || error.name === "InvalidStateError"))
  );
}

function classifyPushRegistrationError(error: unknown): PushRegistrationFailureKind {
  if (!hasReportPushNotificationApis()) {
    return "unsupported";
  }
  if (
    Notification.permission === "denied" ||
    (error instanceof DOMException && error.name === "NotAllowedError")
  ) {
    return "permission-denied";
  }
  if (error instanceof DOMException && error.name === "InvalidStateError") {
    return "service-worker-not-ready";
  }
  if (
    error instanceof DOMException &&
    (error.name === "InvalidAccessError" || error.name === "DataError")
  ) {
    return "invalid-vapid";
  }
  if (
    error instanceof Error &&
    /applicationserverkey|application server key|vapid/i.test(error.message)
  ) {
    return "invalid-vapid";
  }
  if (
    isPushServiceRegistrationError(error) ||
    (error instanceof DOMException && error.name === "AbortError")
  ) {
    return navigator.onLine ? "push-service-unavailable" : "offline";
  }
  if (error instanceof DOMException && error.name === "NotSupportedError") {
    return "unsupported";
  }
  return "unknown";
}

function toPushRegistrationError(error: unknown): PushRegistrationError {
  const kind = classifyPushRegistrationError(error);

  switch (kind) {
    case "permission-denied":
      return new PushRegistrationError(
        kind,
        "Notifications are blocked by your browser or site settings. Allow notifications for this site, then try again.",
      );
    case "service-worker-not-ready":
      return new PushRegistrationError(
        kind,
        "The notification service worker could not become ready. Reload the page and try enabling notifications again.",
      );
    case "push-service-unavailable":
      return new PushRegistrationError(
        kind,
        isBraveBrowser()
          ? "Brave could not reach or initialize its push service. In Brave Settings → Privacy and security, enable ‘Use Google services for push messaging’ if it is disabled, confirm this site can send notifications, then try again."
          : "Your browser's push service could not be reached or initialized. Check the browser's push/notification service settings, then try again.",
      );
    case "offline":
      return new PushRegistrationError(
        kind,
        "Your browser appears to be offline while registering for push notifications. Reconnect to the internet, then try again.",
      );
    case "invalid-vapid":
      return new PushRegistrationError(
        kind,
        "The browser rejected the server's notification key. Reload the page and contact support if the problem continues.",
      );
    case "unsupported":
      return new PushRegistrationError(
        kind,
        "Push notifications are not supported in this browser or browsing mode.",
      );
    default:
      return new PushRegistrationError(
        kind,
        "Unable to enable browser notifications. Check your browser notification settings and try again.",
      );
  }
}

async function getConfiguredVapidPublicKey(): Promise<Uint8Array | null> {
  let config;
  try {
    config = await fetchPushNotificationConfig();
  } catch (error) {
    logPushDiagnostic("unable to load push configuration", error);
    throw new Error(
      "Unable to load SpareLynk notification settings from the server. Check your connection and try again.",
    );
  }

  if (!config.supported || !config.vapidPublicKey.trim()) {
    return null;
  }

  try {
    return decodeAndValidateVapidPublicKey(config.vapidPublicKey);
  } catch {
    return null;
  }
}

async function getActiveRegistration(): Promise<ServiceWorkerRegistration> {
  try {
    return await getAppServiceWorkerRegistration();
  } catch (error) {
    logPushDiagnostic("service worker unavailable", error);
    throw new PushRegistrationError(
      "service-worker-not-ready",
      "Unable to start the app notification service. Reload the page and try again.",
    );
  }
}

async function getExistingSubscription(
  registration: ServiceWorkerRegistration,
  applicationServerKey: Uint8Array,
): Promise<PushSubscription | null> {
  try {
    return await registration.pushManager.getSubscription();
  } catch (error) {
    await logPushRegistrationFailure(
      "PushManager.getSubscription failed",
      error,
      registration,
      applicationServerKey,
    );
    throw toPushRegistrationError(error);
  }
}

async function verifySubscriptionVapidKey(
  subscription: PushSubscription,
  applicationServerKey: Uint8Array,
): Promise<PushSubscription> {
  if (subscriptionUsesVapidKey(subscription, applicationServerKey)) {
    return subscription;
  }

  await removeStaleSubscription(subscription);
  throw new PushRegistrationError(
    "invalid-vapid",
    "The browser created a notification subscription with an unexpected server key. Reload the page and try again.",
  );
}

async function subscribeWithSingleRecovery(
  registration: ServiceWorkerRegistration,
  applicationServerKey: Uint8Array,
): Promise<PushSubscription> {
  const subscribe = (target: ServiceWorkerRegistration) =>
    target.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

  try {
    const subscription = await subscribe(registration);
    return await verifySubscriptionVapidKey(subscription, applicationServerKey);
  } catch (firstError) {
    if (firstError instanceof PushRegistrationError) {
      throw firstError;
    }

    await logPushRegistrationFailure(
      "PushManager.subscribe failed",
      firstError,
      registration,
      applicationServerKey,
    );

    if (!isControlledRetryCandidate(firstError)) {
      throw toPushRegistrationError(firstError);
    }

    logPushDiagnostic("starting controlled push registration recovery", firstError, {
      registration,
    });

    let refreshedRegistration: ServiceWorkerRegistration;
    try {
      refreshedRegistration = await recoverAppServiceWorkerRegistration();
    } catch (recoveryError) {
      logPushDiagnostic("service worker recovery failed", recoveryError, {
        registration,
      });
      throw new PushRegistrationError(
        "service-worker-not-ready",
        "The app notification service could not recover its service worker. Reload the page and try again.",
      );
    }

    let recoveredSubscription: PushSubscription | null;
    try {
      recoveredSubscription =
        await refreshedRegistration.pushManager.getSubscription();
    } catch (subscriptionError) {
      await logPushRegistrationFailure(
        "PushManager.getSubscription failed after recovery",
        subscriptionError,
        refreshedRegistration,
        applicationServerKey,
      );
      throw toPushRegistrationError(subscriptionError);
    }

    if (recoveredSubscription) {
      if (subscriptionUsesVapidKey(recoveredSubscription, applicationServerKey)) {
        return recoveredSubscription;
      }
      await removeStaleSubscription(recoveredSubscription);
    }

    try {
      const subscription = await subscribe(refreshedRegistration);
      return await verifySubscriptionVapidKey(subscription, applicationServerKey);
    } catch (retryError) {
      if (retryError instanceof PushRegistrationError) {
        throw retryError;
      }
      await logPushRegistrationFailure(
        "PushManager.subscribe failed after controlled recovery",
        retryError,
        refreshedRegistration,
        applicationServerKey,
      );
      throw toPushRegistrationError(retryError);
    }
  }
}

export async function getReportPushNotificationState(): Promise<ReportPushNotificationState> {
  if (!hasReportPushNotificationApis()) {
    return "unsupported";
  }
  if (!isSecurePushContext()) {
    return "unavailable";
  }
  if (Notification.permission === "denied") {
    return "denied";
  }

  let applicationServerKey: Uint8Array | null;
  try {
    applicationServerKey = await getConfiguredVapidPublicKey();
  } catch {
    return "unavailable";
  }
  if (!applicationServerKey) {
    return "unconfigured";
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await getActiveRegistration();
  } catch {
    return "unavailable";
  }

  let subscription: PushSubscription | null;
  try {
    subscription = await registration.pushManager.getSubscription();
  } catch (error) {
    await logPushRegistrationFailure(
      "unable to read existing browser subscription",
      error,
      registration,
      applicationServerKey,
    );
    return "unavailable";
  }
  if (!subscription) {
    return "disabled";
  }

  if (!subscriptionUsesVapidKey(subscription, applicationServerKey)) {
    try {
      await removeStaleSubscription(subscription);
      return "disabled";
    } catch {
      return "unavailable";
    }
  }

  try {
    await registerPushSubscription(serializeSubscription(subscription));
  } catch (error) {
    logPushDiagnostic("unable to synchronize existing push subscription", error, {
      registration,
      getSubscriptionSucceeded: true,
      existingSubscription: true,
      vapidKeyMatches: true,
    });
    return "unavailable";
  }
  return "enabled";
}

export async function enableReportPushNotifications(): Promise<ReportPushNotificationState> {
  if (!hasReportPushNotificationApis()) {
    return "unsupported";
  }
  if (!isSecurePushContext()) {
    throw new PushRegistrationError(
      "unsupported",
      "Browser notifications require a secure HTTPS connection. Open this site over HTTPS and try again.",
    );
  }

  const applicationServerKey = await getConfiguredVapidPublicKey();
  if (!applicationServerKey) {
    return "unconfigured";
  }

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    return "denied";
  }

  const registration = await getActiveRegistration();
  let subscription = await getExistingSubscription(
    registration,
    applicationServerKey,
  );

  if (subscription && !subscriptionUsesVapidKey(subscription, applicationServerKey)) {
    await removeStaleSubscription(subscription);
    subscription = null;
  }

  if (!subscription) {
    subscription = await subscribeWithSingleRecovery(
      registration,
      applicationServerKey,
    );
  }

  await verifySubscriptionVapidKey(subscription, applicationServerKey);

  try {
    await registerPushSubscription(serializeSubscription(subscription));
  } catch (error) {
    logPushDiagnostic("unable to persist browser push subscription", error, {
      registration,
      getSubscriptionSucceeded: true,
      existingSubscription: true,
      vapidKeyMatches: true,
    });
    throw new Error(
      "The browser notification was created, but the server could not save it. Check your connection and try again.",
    );
  }
  return "enabled";
}

export async function disableReportPushNotifications(): Promise<ReportPushNotificationState> {
  if (!hasReportPushNotificationApis()) {
    return "unsupported";
  }
  if (!isSecurePushContext()) {
    return "unavailable";
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await getActiveRegistration();
  } catch {
    return "unavailable";
  }

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return Notification.permission === "denied" ? "denied" : "disabled";
  }

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  try {
    await removePushSubscription(endpoint);
  } catch (error) {
    // The push provider will eventually return 404/410 and the backend will clean up the stale endpoint.
    logPushDiagnostic("unable to remove backend push subscription", error, {
      registration,
      getSubscriptionSucceeded: true,
      existingSubscription: true,
    });
  }
  return "disabled";
}

export type PushNotificationState = ReportPushNotificationState;
export const getPushNotificationState = getReportPushNotificationState;
export const enablePushNotifications = enableReportPushNotifications;
export const disablePushNotifications = disableReportPushNotifications;
